// User-specific storage utility to keep cache separate for different users

/**
 * Gets the current username from localStorage
 * @returns {string|null} The current username or null if not logged in
 */
export const getCurrentUsername = () => {
    return localStorage.getItem('anilist_username');
};

/**
 * Creates a user-specific key by prefixing with username
 * @param {string} key - The original key
 * @returns {string} The user-specific key
 */
export const getUserKey = (key) => {
    const username = getCurrentUsername();
    if (!username) {
        // If no username, use global key (for login process)
        return key;
    }
    return `${username}_${key}`;
};

/**
 * User-specific wrapper for localStorage.setItem
 * @param {string} key - The key to store
 * @param {string} value - The value to store
 */
export const setUserItem = (key, value) => {
    try {
        const userKey = getUserKey(key);
        localStorage.setItem(userKey, value);
    } catch (error) {
        // Ignore quota errors - data will be in IndexedDB
        if (error.name !== 'QuotaExceededError') {
            console.error('localStorage error:', error);
        }
    }
};

/**
 * User-specific wrapper for localStorage.getItem
 * @param {string} key - The key to retrieve
 * @returns {string|null} The stored value or null
 */
export const getUserItem = (key) => {
    const userKey = getUserKey(key);
    return localStorage.getItem(userKey);
};

/**
 * User-specific wrapper for localStorage.removeItem
 * @param {string} key - The key to remove
 */
export const removeUserItem = (key) => {
    const userKey = getUserKey(key);
    localStorage.removeItem(userKey);
};

/**
 * Clears all user-specific data (called on logout)
 */
export const clearUserData = () => {
    const username = getCurrentUsername();
    if (!username) return;

    // Get all keys in localStorage
    const keys = Object.keys(localStorage);

    // Remove all keys that start with username prefix
    keys.forEach(key => {
        if (key.startsWith(`${username}_`)) {
            localStorage.removeItem(key);
        }
    });
};

/**
 * Migrates existing non-prefixed data to user-prefixed data (one-time migration)
 * Call this after login to move existing cache to the new user-specific format
 */
export const migrateToUserSpecificStorage = () => {
    const username = getCurrentUsername();
    if (!username) return;

    const keysToMigrate = [
        'anilist_cache_data',
        'anilist_cache_timestamp',
        'anilist_groups_cache',
        'removed_anime',
        'anime_name_preference',
        'time_settings_hours_per_day',
        'time_settings_quick_win_hours',
        'link_settings_watch'
    ];

    keysToMigrate.forEach(key => {
        const userKey = `${username}_${key}`;
        // Only migrate if user-specific key doesn't exist but global key does
        if (!localStorage.getItem(userKey) && localStorage.getItem(key)) {
            const value = localStorage.getItem(key);
            try {
                localStorage.setItem(userKey, value);
            } catch (error) {
                // Skip large data that exceeds quota
                if (error.name !== 'QuotaExceededError') {
                    console.error('Migration error:', error);
                }
            }
            // Don't remove global key yet in case something goes wrong
        }
    });
};
